/**
 * REST client for the artium-reports service: read-only reports served from
 * Snowflake (Sales Agent Performance and friends). Cross-origin: the service
 * allowlists CMS origins via CORS. Auth is the same CMS-issued JWT the rest
 * of adminv2 sends, verified by artium-reports' CmsAdminGuard (any
 * ROLE_ADMIN*) with the shared JWT_SECRET_KEY/JWT_CUSTOM_KEY.
 *
 * REACT_APP_REPORTS_BASEURL unset outside local dev means the service isn't
 * deployed for this environment yet; pages say so instead of failing.
 */

const configured = (process.env.REACT_APP_REPORTS_BASEURL || '').replace(/\/$/, '');

export const REPORTS_BASEURL =
  configured || (process.env.NODE_ENV === 'development' ? 'http://localhost:3500' : '');

export const REPORTS_ENABLED = Boolean(REPORTS_BASEURL);

export async function reportsApi<T = unknown>(
  path: string,
  extraHeaders: Record<string, string> = {},
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const rawToken = localStorage.getItem('authToken') || '';
  const authHeader = rawToken
    ? rawToken.startsWith('Bearer ')
      ? rawToken
      : `Bearer ${rawToken}`
    : '';

  const res = await fetch(`${REPORTS_BASEURL}${path}`, {
    method: init.method ?? 'GET',
    headers: {
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...extraHeaders,
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = Array.isArray((body as any).message)
      ? (body as any).message.join(', ')
      : (body as any).message || res.statusText;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Sales Agent Performance — shapes from artium-reports docs/API.md
// ---------------------------------------------------------------------------

export const COHORTS = ['D1', 'D2', 'D3', 'D4-D7', 'D8-D14', 'D15-D30', 'D30+'] as const;
export type Cohort = (typeof COHORTS)[number];

export type SalesCountry = 'India' | 'USA' | 'ROW';

export type SalesRole = 'SALES_ADMIN' | 'SALES_AGENT';

/** Who the server scoped a response to. The page renders from this, not from its own guess. */
export interface SalesViewer {
  role: SalesRole;
  email: string | null;
}


/** Roles from the login payload and the profile API, normalized with a ROLE_ prefix. */
function storedRoleStrings(): string[] {
  const read = (key: string): { role?: unknown; authorities?: unknown } => {
    try {
      return JSON.parse(localStorage.getItem(key) || '{}');
    } catch {
      return {};
    }
  };
  const sources = [read('userProfileData'), read('profileData')];
  return sources
    .flatMap((source) => [source.role, source.authorities])
    .flatMap((value) => (Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : []))
    .filter((role): role is string => typeof role === 'string')
    .map((role) => {
      const normalized = role.trim().toUpperCase();
      if (!normalized) return '';
      return normalized.startsWith('ROLE_') ? normalized : `ROLE_${normalized}`;
    })
    .filter(Boolean);
}

/**
 * The signed-in user's sales role, read the way artium-reports reads it:
 * ROLE_ADMIN (super admin) or any role ending SALES_ADMIN → SALES_ADMIN, even
 * when a sales-agent authority is also on the token; ending SALES_AGENT →
 * SALES_AGENT; anyone else → null, so the sales screens are hidden (the
 * server refuses them). Local development falls back to SALES_ADMIN, matching
 * the server's ALLOW_UNAUTHENTICATED. Menus and labels only: the server
 * decides what data is shown.
 */
export function currentSalesRole(): SalesRole | null {
  const roles = storedRoleStrings();
  if (roles.includes('ROLE_ADMIN') || roles.some((role) => role.endsWith('SALES_ADMIN'))) return 'SALES_ADMIN';
  if (roles.some((role) => role.endsWith('SALES_AGENT'))) return 'SALES_AGENT';
  return process.env.NODE_ENV === 'development' ? 'SALES_ADMIN' : null;
}

export interface Period { from: string; to: string }

export interface SalesOwnerRow {
  ownerId: string;
  /** Lower-cased Salesforce owner email; null for unassigned leads. */
  ownerEmail: string | null;
  leadOwner: string;
  country: SalesCountry;
  assigned: number;
  converted: number;
  conversionPct: number;
  previousConversionPct: number;
  trend: 'up' | 'down' | 'flat';
  cohorts: Record<Cohort, number>;
  revenue: number;
  atv: number;
  totalTalkMinutes: number;
  /** Per completed call. */
  avgTalkMinutes: number;
  /** Days in the range with any talk time. */
  talkDays: number;
  /** Talk time ÷ days with calls. */
  avgTalkPerDayMinutes: number;
}

export interface SalesSummary {
  viewer: SalesViewer;
  period: Period;
  previousPeriod: Period;
  lastSyncedAt: string | null;
  totals: {
    assigned: number;
    converted: number;
    cohorts: { cohort: Cohort; count: number; pct: number }[];
  };
  owners: SalesOwnerRow[];
}

export interface SalesDailyRow {
  date: string;
  ownerId: string;
  leadOwner: string;
  country: SalesCountry;
  assigned: number;
  converted: number;
  revenue: number;
}

export interface SalesOwnerMonthly {
  viewer: SalesViewer;
  ownerId: string;
  months: { month: string; assigned: number; converted: number; conversionPct: number; revenue: number }[];
}

const qs = (params: Record<string, string | number | boolean | undefined>): string => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== false) p.set(k, String(v));
  });
  return p.toString();
};

/** The summary's comparison period: the range moved back a month, a week or a day. */
export type SalesCompare = 'month' | 'week' | 'day';

export const fetchSalesSummary = (from: string, to: string, refresh = false, compare: SalesCompare = 'month') =>
  reportsApi<SalesSummary>(`/v1/sales-agent-performance/summary?${qs({ from, to, refresh, compare })}`);

export const fetchSalesDaily = (from: string, to: string, refresh = false) =>
  reportsApi<{ viewer: SalesViewer; period: Period; rows: SalesDailyRow[] }>(
    `/v1/sales-agent-performance/daily?${qs({ from, to, refresh })}`,
  );

export interface StandingMonth { month: string; rank: number; of: number; revenue: number; conversions: number }

/** "Where I stand": rank by revenue among active agents of the same region. Only the viewer's own position. */
export interface SalesStanding {
  month: string;
  email: string;
  /** False: not an active agent on Sales → Sales Agents. */
  inPool: boolean;
  /** Null with inPool: the agent's region isn't set. */
  region: string | null;
  poolSize: number;
  current: (StandingMonth & {
    topPct: number;
    behindNext: { rank: number; amount: number } | null;
    aheadOfNext: { rank: number; amount: number } | null;
  }) | null;
  previous: StandingMonth | null;
  /** Places gained since last month; positive = up. */
  change: number | null;
  trend: StandingMonth[];
}

/** "This week's incentive": the agent's plan worked out against the week so far. */
export interface SalesWeeklyIncentive {
  weekStart: string;
  label: string;
  /** Last day counted: today while the week runs, else Saturday. */
  through: string;
  inProgress: boolean;
  email: string;
  isAgent: boolean;
  agentName: string | null;
  plan: ({ id: string; name: string; market: IncentiveMarket } & IncentivePlanRules) | null;
  demoTarget: number | null;
  actuals: IncentiveWeek;
  breakdown: IncentiveBreakdown | null;
  /** False until package session counts are in Snowflake: no session bonus is counted. */
  sessionBonusTracked: boolean;
}

/** The current week unless `week` (a Sunday) is given. An agent gets their own; an admin passes the email. */
export const fetchWeeklyIncentive = (email?: string, week?: string, refresh = false) =>
  reportsApi<SalesWeeklyIncentive>(`/v1/sales-agent-performance/weekly-incentive?${qs({ week, email, refresh })}`);

/** An agent always gets their own standing; an admin passes the agent's email. */
export const fetchSalesStanding = (month: string, email?: string, refresh = false) =>
  reportsApi<SalesStanding>(`/v1/sales-agent-performance/standing?${qs({ month, email, refresh })}`);

export const fetchSalesOwnerMonthly = (ownerId: string, month: string, months = 6, refresh = false) =>
  reportsApi<SalesOwnerMonthly>(
    `/v1/sales-agent-performance/owners/${encodeURIComponent(ownerId)}/monthly?${qs({ month, months, refresh })}`,
  );

// ---------------------------------------------------------------------------
// Sales admin — agents, targets, incentive plans (SALES_ADMIN only)
// ---------------------------------------------------------------------------

/** SALES_ADMIN only; the server checks the role on the login token. */
const adminGet = <T,>(path: string) => reportsApi<T>(path);
const adminSend = <T,>(method: 'POST' | 'PUT' | 'PATCH', path: string, body: unknown) =>
  reportsApi<T>(path, {}, { method, body });

export const SALES_REGIONS = ['India', 'USA', 'ROW'] as const;

export interface SalesAgent {
  id: string;
  email: string;
  name: string;
  salesforceUserId: string | null;
  region: string | null;
  team: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SalesAgentCandidate {
  email: string;
  name: string;
  salesforceUserId: string | null;
  region: string | null;
  recentDemos: number;
}

export interface SalesTargetRow {
  agent: SalesAgent;
  target: {
    /** Optional; revenue and conversion targets are the plan's gates. */
    demoTarget: number | null;
    incentivePlan: { id: string; name: string; minRevenue: number; minConversions: number } | null;
    /** Null when set for this very week; else the Sunday it carries forward from. */
    inheritedFrom: string | null;
    updatedAt: string;
  } | null;
}

/** Weekly incentive plan rules — see artium-reports src/sales-admin/incentive.ts. */
export type IncentiveMarket = 'DOMESTIC' | 'INTERNATIONAL';
export interface ConversionPctGate { country: 'India' | 'USA' | 'ROW'; minPct: number }
export interface SessionTier { minSessions: number; label: string; amount: number }

export interface IncentivePlanRules {
  minRevenue: number;
  minConversions: number;
  conversionPctGates: ConversionPctGate[];
  conversionPayoutFrom: number;
  conversionPayoutBase: number;
  conversionPayoutPerExtra: number;
  revenuePct: number;
  sessionTiers: SessionTier[];
}

export interface IncentivePlan extends IncentivePlanRules {
  id: string;
  name: string;
  description: string | null;
  market: IncentiveMarket;
  isActive: boolean;
  /** Sundays of the weeks whose targets use this plan, newest first. */
  usedInWeeks: string[];
  lockedReason: string | null;
  updatedAt: string;
}

export interface IncentiveWeek {
  revenue: number;
  conversions: number;
  byCountry: Partial<Record<'India' | 'USA' | 'ROW', { assigned: number; converted: number }>>;
  saleSessions: number[];
}

export interface IncentiveBreakdown {
  eligible: boolean;
  gates: { key: string; label: string; required: number; actual: number | null; met: boolean; skipped: boolean }[];
  conversionPayout: number;
  revenuePayout: number;
  sessionBonus: number;
  sessionBonusByTier: { label: string; minSessions: number; amount: number; sales: number }[];
  total: number;
  potentialTotal: number;
}

export const fetchSalesAgents = () => adminGet<SalesAgent[]>('/v1/sales-agents');
export const fetchAgentCandidates = (refresh = false) =>
  adminGet<SalesAgentCandidate[]>(`/v1/sales-agents/candidates${refresh ? '?refresh=true' : ''}`);
export const createSalesAgent = (body: { email: string; name: string; region?: string | null; team?: string | null }) =>
  adminSend<SalesAgent>('POST', '/v1/sales-agents', body);
export const importSalesAgents = (emails: string[]) =>
  adminSend<{ created: SalesAgent[]; skipped: string[] }>('POST', '/v1/sales-agents/import', { emails });
export const updateSalesAgent = (id: string, body: Partial<Pick<SalesAgent, 'email' | 'name' | 'region' | 'team' | 'isActive'>>) =>
  adminSend<SalesAgent>('PATCH', `/v1/sales-agents/${id}`, body);

/** Targets are weekly (Sunday–Saturday); a week is named by its Sunday, "YYYY-MM-DD". */
export const fetchSalesTargets = (week: string) =>
  adminGet<{ weekStart: string; label: string; locked: boolean; rows: SalesTargetRow[] }>(`/v1/sales-targets?week=${week}`);
export const saveSalesTarget = (
  agentId: string,
  week: string,
  body: { demoTarget: number | null; incentivePlanId: string | null },
) => adminSend('PUT', `/v1/sales-targets/${agentId}/${week}`, body);

export const fetchIncentivePlans = () => adminGet<IncentivePlan[]>('/v1/incentive-plans');
type PlanBody = IncentivePlanRules & { name: string; description?: string | null; market: IncentiveMarket; isActive?: boolean };
export const createIncentivePlan = (body: PlanBody) =>
  adminSend<IncentivePlan>('POST', '/v1/incentive-plans', body);
export const updateIncentivePlan = (id: string, body: PlanBody) =>
  adminSend<IncentivePlan>('PUT', `/v1/incentive-plans/${id}`, body);
export const previewIncentive = (rules: IncentivePlanRules, week: IncentiveWeek) =>
  adminSend<IncentiveBreakdown>('POST', '/v1/incentive-plans/preview', { ...rules, ...week });
